import * as Elevated from '@pitaman71/omniglot-introspect';

class _BindingDomain extends Elevated.Domain<Binding<string>> {
    asSchema() { return { status: "string", objectId: "string" } }
    asJSON() { return {
        to(the: Binding<string>|null): Elevated.JSONValue {
            if(the === null) return null;
            return {
                status: the.status,
                objectId: the.objectId
            };
        },
        from(json: Elevated.JSONValue, options?: { onError?: (error: Elevated.Error) => void }): Binding<string>|null {
            if(typeof json !== 'object' || Array.isArray(json) || !json?.status || !json?.objectId) {
                if(options?.onError) options.onError({ type: { expected: 'object' } })
                return null;
            }
            if(json.status !== 'new' && json.status !== 'bound' && json.status !== 'free') {
                if(options?.onError) options.onError({ properties: { status: { status: 'malformed', details: { format: { expected: { tokenType: 'new|bound|free' } }}}}})
                return null;
            }
            const result = new Binding<string>();
            if(typeof json.objectId !== 'string') {
                if(options?.onError) options.onError({ properties: { objectId: { status: 'malformed', details: { type: { expected: 'string' }}}}})
                return null;
            }
            result.status = json.status;
            result.objectId = json.objectId;
            return result;
        }
    }}
    asString() { return {
        to(the: Binding<string>) {
            return `${the.objectId}` || '';
        },
        from(text: string) {
            return Binding.from_bound(text);
        }
    }}
    cmp() { return undefined}
}
export const BindingDomain = new _BindingDomain();

export class Binding<ObjectIdType extends string|object> { 
    status: 'new'|'bound'|'free' = 'new';
    objectId?: ObjectIdType;

    isAssigned() { return this.objectId }
    doAssign(value: ObjectIdType) { 
        if(this.objectId === value) return true;
        if(this.objectId === undefined) {
            this.objectId = value;
            return true;
        }
        return false;
    };

    static cmp<ObjectIdType extends string|object>(a: undefined|Binding<ObjectIdType>|Binding<ObjectIdType>[], b: undefined|Binding<ObjectIdType>|Binding<ObjectIdType>[]): -1|0|1|undefined {
        if(a === undefined || b === undefined) {
            return undefined;
        } else if(Array.isArray(a) && !Array.isArray(b)) {
            return Binding.cmp(a,[b]);
        } else if(!Array.isArray(a) && Array.isArray(b)) {
            return Binding.cmp([a], b);
        } else if(Array.isArray(a) && Array.isArray(b)) {
            if(a.length < b.length) return -1;
            else if(a.length > b.length) return 1;
            for(let i: number = 0; i < a.length ; ++i) {
                const code: -1|0|1|undefined = Binding.cmp(a[i], b[i]);
                if(code !== 0) return code;
            }
        } else if(!Array.isArray(a) && !Array.isArray(b)) {
            if(a.objectId === undefined || b.objectId === undefined) {
                if(a.objectId === undefined && b.objectId !== undefined) return -1;
                if(a.objectId !== undefined && b.objectId === undefined) return 1;
                return 0;
            }
            if(a.objectId < b.objectId) return -1;
            if(a.objectId > b.objectId) return 1;
        }
        return 0;
    }

    static from_new<ObjectIdType extends string|object>(): Binding<ObjectIdType> {
        const result = new Binding<ObjectIdType>();
        result.status = 'new';
        return result;
    }

    static from_bound<ObjectIdType extends string|object>(val: ObjectIdType): Binding<ObjectIdType> {
        const result = new Binding<ObjectIdType>();
        result.status = 'bound';
        result.objectId = val;
        return result;
    }

    static from_free<ObjectIdType extends string|object>(): Binding<ObjectIdType> {
        const result = new Binding<ObjectIdType>();
        result.status = 'free';
        return result;
    }
};

export type BindingType<ObjectIdType extends string|object = string|object> = { [key: string]: Binding<ObjectIdType>};

export function cmp_objects<ObjectIdType extends string|object>(a: BindingType<ObjectIdType>,b: BindingType<ObjectIdType>) {
    for (let key of Object.getOwnPropertyNames(a)) {
        const code = Binding.cmp(a[key], b[key]);
        if(code !== 0) return code;
    }
    for (let key of Object.getOwnPropertyNames(b)) {
        const code = Binding.cmp(a[key], b[key]);
        if(code !== 0) return code;
    }
    return 0;
}    

export function cmp_arrays<ObjectIdType extends string|object>(a: BindingType<ObjectIdType>[], b: BindingType<ObjectIdType>[]) {
    if(a.length < b.length) return -1;
    if(a.length > b.length) return 1;
    for(let i = 0; i < a.length;i++) {
        const code = cmp_objects(a[i], b[i]);
        if(code != 0) return code;
    }
    return 0;
}

export function contains<ObjectIdType extends string|object>(a: BindingType<ObjectIdType>[], b: BindingType<ObjectIdType>) {
    return a.reduce((result, _item) => (
        result || (cmp_objects(_item, b) === 0)
    ), false);
}
